/**
 * Jotai atoms for team feature state management
 * Manages pagination, selection, and filter state
 */

import { atom } from 'jotai';

import { TEAM_MEMBER_STATUS, type TeamMemberStatus } from '@ekhodealer/ekho-common';

import type { Selection } from 'react-aria-components';
import type { SearchTeamMembersParams } from '../api/useSearchTeamMembers';

// Tab filter atom
export const teamTabFilterAtom = atom<TeamMemberStatus>(TEAM_MEMBER_STATUS.ALL);

// Pagination atoms
export const currentPageAtom = atom<number>(1);
export const itemsPerPageAtom = atom<number>(10);

// Selection atoms
export const selectedTeamMembersAtom = atom<Selection>(new Set<string>());

// Derived atom for API parameters
export const teamParamsAtom = atom((get): SearchTeamMembersParams => {
	const statusFilter = get(teamTabFilterAtom);
	const currentPage = get(currentPageAtom);
	const itemsPerPage = get(itemsPerPageAtom);

	const params: SearchTeamMembersParams = {
		limit: itemsPerPage,
		page: currentPage - 1, // Convert to 0-indexed for API
		timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
	};

	if (statusFilter && statusFilter !== TEAM_MEMBER_STATUS.ALL) {
		params.status = statusFilter;
	}

	return params;
});

// Action atom to handle tab filter changes
export const updateTeamTabFilterAtom = atom(
	(get) => get(teamTabFilterAtom),
	(_get, set, newTab: TeamMemberStatus) => {
		set(teamTabFilterAtom, newTab);
		set(currentPageAtom, 1);
	},
);

// Update atoms for selection
export const updateSelectedTeamMembersAtom = atom(null, (_get, set, newSelection: Selection) => {
	set(selectedTeamMembersAtom, newSelection);
});

// Update atom for items per page
export const updateItemsPerPageAtom = atom(null, (_get, set, newItemsPerPage: number) => {
	set(itemsPerPageAtom, newItemsPerPage);
	set(currentPageAtom, 1); // Reset to first page when changing items per page
});
